import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '@/components/shared/PageHeader';
import { WorkOrderStatusBadge, AssetStatusBadge, ConditionBadge } from '@/components/shared/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ClipboardList, Package, Truck, Building2, Calendar, User, MapPin,
  Play, ArrowLeft, CheckCircle2, ClipboardCheck, Shield, Activity,
} from 'lucide-react';
import { useWorkOrder, useIntakeLines, useWorkOrderAssets, useUpdateWorkOrderStatus } from '@/hooks/useWorkOrders';
import { useWorkOrderActivity } from '@/hooks/useActivityLog';
import { useSites } from '@/hooks/useSettings';
import { getActionLabel } from '@/services/activityLogService';
import DestructionCertDialog from '@/components/compliance/DestructionCertDialog';
import { toast } from 'sonner';

export default function WorkOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [certOpen, setCertOpen] = useState(false);

  const { data: wo, isLoading: woLoading } = useWorkOrder(id);
  const { data: intakeLines = [] } = useIntakeLines(id);
  const { data: assets = [] } = useWorkOrderAssets(id);
  const { data: sites = [] } = useSites();
  const { data: activityLogs = [] } = useWorkOrderActivity(id, assets.map((a) => a.id));
  const updateStatus = useUpdateWorkOrderStatus();

  if (woLoading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-40 w-full" /></div>;
  }

  if (!wo) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Work order not found.</p>
      </div>
    );
  }

  const site = sites.find((s) => s.id === wo.site_id);
  const progressPct = wo.assets_received > 0 ? Math.round((wo.assets_processed / wo.assets_received) * 100) : 0;
  const canPerformPickup = ['scheduled', 'in_progress'].includes(wo.status);
  const canStartIntake = ['scheduled', 'in_progress'].includes(wo.status);

  // Job completion logic (Steps 23, 27)
  const completedAssets = assets.filter((a) => a.status === 'completed').length;
  const totalAssets = assets.length;
  const pendingAssets = totalAssets - completedAssets;
  const allCompleted = totalAssets > 0 && pendingAssets === 0;
  const canComplete = allCompleted && wo.status !== 'completed';

  function handleCompleteJob() {
    if (!allCompleted) {
      toast.error(`Cannot complete: ${pendingAssets} items still pending`);
      return;
    }
    updateStatus.mutate(
      { id: wo.id, status: 'completed' },
      {
        onSuccess: () => {
          toast.success('Work order marked as completed');
          navigate('/work-orders');
        },
      }
    );
  }

  return (
    <>
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/work-orders')} className="gap-1.5 text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> Work Orders
        </Button>
      </div>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground font-mono">{wo.work_order_number}</h1>
            <WorkOrderStatusBadge status={wo.status} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{wo.customer_name} · {wo.company}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canPerformPickup && (
            <Button onClick={() => navigate(`/work-orders/${wo.id}/pickup`)} className="gap-2">
              <Truck className="h-4 w-4" /> Complete Pickup
            </Button>
          )}
          {canStartIntake && (
            <Button variant="outline" onClick={() => navigate(`/work-orders/${wo.id}/intake`)} className="gap-2">
              <Play className="h-4 w-4" /> Start Intake
            </Button>
          )}
          {assets.length > 0 && wo.status !== 'completed' && (
            <Button variant="outline" onClick={() => navigate(`/work-orders/${wo.id}/intake-summary`)} className="gap-2">
              <ClipboardCheck className="h-4 w-4" /> Intake Summary
            </Button>
          )}
          {assets.length > 0 && (
            <Button variant="outline" onClick={() => setCertOpen(true)} className="gap-2">
              <Shield className="h-4 w-4" /> Destruction Cert
            </Button>
          )}
          {canComplete && (
            <Button onClick={handleCompleteJob} className="gap-2 bg-status-complete hover:bg-status-complete/90">
              <CheckCircle2 className="h-4 w-4" /> Complete Job
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            {wo.work_order_type === 'pickup' ? <Truck className="h-5 w-5 text-primary" /> : <Building2 className="h-5 w-5 text-primary" />}
            <div>
              <p className="text-xs text-muted-foreground">Type</p>
              <p className="text-sm font-medium capitalize text-foreground">{wo.work_order_type}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <MapPin className="h-5 w-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Site</p>
              <p className="text-sm font-medium text-foreground">{site?.name ?? wo.site_id}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <Calendar className="h-5 w-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Scheduled</p>
              <p className="text-sm font-medium text-foreground">{wo.scheduled_date}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <User className="h-5 w-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Operator</p>
              <p className="text-sm font-medium text-foreground">{wo.assigned_operator || '—'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Processing Progress (Step 23) */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-foreground">Processing Progress</p>
            <p className="text-xs text-muted-foreground">{completedAssets} / {totalAssets} completed</p>
          </div>
          <Progress value={totalAssets > 0 ? Math.round((completedAssets / totalAssets) * 100) : 0} className="h-2" />
          {pendingAssets > 0 && (
            <p className="text-[10px] text-muted-foreground mt-1">
              {pendingAssets} items still pending — job cannot be completed until all items have final disposition
            </p>
          )}
          {allCompleted && wo.status !== 'completed' && (
            <p className="text-[10px] text-status-complete mt-1 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> All items completed — ready to close
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pickup details */}
      {wo.work_order_type === 'pickup' && (wo.pickup_address || wo.driver) && (
        <Card className="mb-6">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm font-semibold text-foreground">Pickup Details</p>
            {wo.pickup_address && <p className="text-sm text-muted-foreground"><span className="text-foreground font-medium">Address:</span> {wo.pickup_address}</p>}
            {wo.driver && <p className="text-sm text-muted-foreground"><span className="text-foreground font-medium">Driver:</span> {wo.driver} {wo.vehicle && `· ${wo.vehicle}`}</p>}
          </CardContent>
        </Card>
      )}

      {/* Intake Lines */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Intake Lines</p>
            <Badge variant="secondary" className="text-xs">{intakeLines.length} lines</Badge>
          </div>
          {intakeLines.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No intake lines yet. Start intake to begin receiving assets.</p>
          ) : (
            <div className="space-y-3">
              {intakeLines.map((line) => {
                const pct = line.quantity_total > 0 ? Math.round((line.quantity_serialized / line.quantity_total) * 100) : 0;
                return (
                  <div key={line.id} className="rounded-lg border border-border p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground">{line.category_name}</p>
                        <Badge variant="outline" className="text-[10px] capitalize">{line.intake_mode}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{line.quantity_serialized} / {line.quantity_total}</p>
                    </div>
                    <Progress value={pct} className="h-1.5" />
                    <p className="mt-1 text-[10px] text-muted-foreground">{line.quantity_remaining} remaining</p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assets */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Assets</p>
            <Badge variant="secondary" className="text-xs">{assets.length} assets</Badge>
          </div>
          {assets.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No assets received yet.</p>
          ) : (
            <div className="space-y-2">
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  className="flex items-center justify-between rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/assets/${asset.id}`)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-mono font-medium text-foreground">{asset.internal_barcode}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {asset.category_name}
                      {asset.manufacturer_name && ` · ${asset.manufacturer_name}`}
                      {asset.model && ` ${asset.model}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <ConditionBadge condition={asset.condition} />
                    <AssetStatusBadge status={asset.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Log (Step 22) */}
      {activityLogs.length > 0 && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold text-foreground">Activity Log</p>
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {activityLogs.map((log) => {
                const { label, icon } = getActionLabel(log.action);
                return (
                  <div key={log.id} className="flex items-start gap-3">
                    <span className="text-base mt-0.5">{icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-medium text-foreground">{label}</p>
                        <span className="text-[10px] text-muted-foreground">
                          {log.user_name}
                        </span>
                      </div>
                      {log.details && (
                        <p className="text-[10px] text-muted-foreground truncate">{log.details}</p>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {new Date(log.timestamp).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      {wo.notes && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-foreground mb-1">Notes</p>
            <p className="text-sm text-muted-foreground">{wo.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Destruction Certificate Dialog */}
      <DestructionCertDialog
        open={certOpen}
        onOpenChange={setCertOpen}
        assets={assets}
        customerName={wo.customer_name}
        workOrderNumber={wo.work_order_number}
        customerEmail={wo.email}
      />
    </>
  );
}
